import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import { NavigationSubCategoriesApi } from 'graphql/generated';
import useTranslation from 'next-translate/useTranslation';
import { memo } from 'react';
import twMerge from 'utils/twMerge';

type NavigationCategoryType = NonNullable<NavigationSubCategoriesApi['category']>;

type SubCategoryProps = {
    item: NavigationCategoryType['children'][number];
    className?: string;
};

const SubCategory = memo<SubCategoryProps>(({ item, className }) => {
    const { t } = useTranslation();

    const itemChildren = item.children;

    return (
        <li className={twMerge('relative min-h-[108px] pb-7 pl-[100px] notXl:pl-[80px]', className)}>
            <div className="absolute left-0 top-0 flex h-20 w-20 items-center justify-center notXl:h-16 notXl:w-16">
                <Image
                    alt={item.name}
                    className="max-h-full max-w-full mix-blend-multiply"
                    height="80"
                    src={item.mainImage?.url ?? null}
                    width="80"
                />
            </div>
            <div>
                <ExtendedNextLink
                    className="text-description font-medium leading-6 text-primary no-underline hover:underline"
                    href={item.link || item.slug}
                    type={item.link ? 'static-presumed-category' : 'category'}
                >
                    {item.name}
                </ExtendedNextLink>
            </div>
            {itemChildren.length > 0 && (
                <ul className="mt-2 text-[15px] tracking-wider">
                    {itemChildren.slice(0, 3).map((childItem, index) => (
                        <li key={index} className="py-0.5">
                            <ExtendedNextLink
                                className="text-primary no-underline hover:underline"
                                href={childItem.link || childItem.slug}
                                type={childItem.link ? 'static-presumed-category' : 'category'}
                            >
                                {childItem.name}
                            </ExtendedNextLink>
                        </li>
                    ))}
                    {itemChildren.length > 3 && (
                        <li>
                            <ExtendedNextLink className="text-primary underline" href={item.slug} type="category">
                                {t('more categories')}
                            </ExtendedNextLink>
                        </li>
                    )}
                </ul>
            )}
        </li>
    );
});

SubCategory.displayName = 'SubCategory';
export default SubCategory;
